import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { State } from 'xstate';
import {
  ApolloClient,
  ApolloProvider as ApolloContextProvider,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client';
import { persistCacheSync, LocalStorageWrapper } from 'apollo3-cache-persist';
import { setContext } from '@apollo/client/link/context';
import { Module, defaultModules } from '../config';
import { WizardContext } from '../contexts';
import ComparatorContext, {
  ActionContexts,
  getStorage,
  IBaseComparatorContext,
  IComparatorContext,
  ComparatorStep,
  defaultComparatorContext,
} from '../contexts/comparator/comparator-context';
import { IPageBuilderContext, PageBuilderContext } from '../contexts';
import { IComparatorApiService } from '../services/comparator-api';
import { FormField } from '../state-machines/form-machine';
import {
  wizardMachine as wizard,
  WizardEvent,
  WizardState,
} from '../state-machines/wizard-machine';
import { PageLayout } from './PageBuilder';
import { Routing } from './Routing';
import { KiziLogger } from './Logger';
import { useWizard, UseWizardOptions, WizardInterpreter } from './Wizard';
import { BrowserRouter as Router } from 'react-router-dom';
import { GraphComparisonApiService } from '../services/comparator-api/comparator-graph-api';
import { GraphLegacyApiService } from '../services/comparator-api/comparator-graph-legacy-api';

export const LOCAL_STORAGE_BASE_KEY = 'kizi-comparator';

export interface ComparatorProps
  extends Omit<UseWizardOptions, 'startIndex'>,
    Partial<Omit<IComparatorContext, 'api'>> {
  startIndex?: number;
  steps: ComparatorStep[];
  layout: PageLayout[];
  loadFn: () => Promise<unknown>;
  wizardCallback?: (service: WizardInterpreter) => void;
  name: string;
  api: IComparatorApiService;
  modules?: Module[];
  defaultStep?: string;
  getPeregrineSetupEvent?: any;
  isPartner?: boolean;
}

type GetStateFromLocalStorage = IComparatorContext['getStateFromLocalStorage'];

export const getStateFromLocalStorage =
  (name: string): GetStateFromLocalStorage =>
  () => {
    const identifier = `${LOCAL_STORAGE_BASE_KEY}-${name}`;
    const state = localStorage.getItem(identifier);
    if (!state) return null;
    try {
      return wizard.resolveState(State.create(JSON.parse(state)));
    } catch (e) {
      console.warn(
        'state from storage was invalid, defaulting to initial state'
      );
      return null;
    }
  };

type PersistStateInLocalStorage =
  IComparatorContext['persistStateInLocalStorage'];

export const persistStateInLocalStorage =
  (name: string): PersistStateInLocalStorage =>
  (state) => {
    const identifier = `${LOCAL_STORAGE_BASE_KEY}-${name}`;
    try {
      localStorage.setItem(identifier, JSON.stringify(state));
      return true;
    } catch (e) {
      return false;
    }
  };

const persistOnTransition =
  (name: string) => (state: WizardState, e: WizardEvent) => {
    if (!state) return;
    const splittedEventName = e.type.split('.');
    const shouldPersist =
      (splittedEventName[0] === 'done' &&
        splittedEventName[1] === 'invoke' &&
        state.context.initialSteps
          .map((s) => s.name)
          .includes(splittedEventName[2])) ||
      (e.type === 'GOTO' &&
        state.context.initialSteps.every((s) => {
          return s.fields.every((f) => {
            return !f.errors || f.errors.length === 0;
          });
        }));
    if (shouldPersist) {
      persistStateInLocalStorage(name)(state);
    }
  };

const getGlobalFieldsFromStorage = ({ globalFields, previousState }) => {
  const updatedGlobalFields = globalFields;
  if (previousState && previousState.context) {
    for (const globalField of previousState.context.globalFields ?? []) {
      const i = updatedGlobalFields.findIndex(
        (f) => f.name === globalField.name
      );
      if (i > -1) {
        updatedGlobalFields[i].value = globalField.value;
      }
    }
  }
  return updatedGlobalFields;
};

export const getErrorsAndValuesFromLocalStorage = (
  {
    steps: initialSteps,
    globalFields = [],
  }: { steps: ComparatorStep[]; globalFields?: FormField[] },
  previousState: Partial<WizardState> | null
) => {
  // We only get the values from the previous state so that changes in other rules apply without clearing the localStorage
  // Deep copy the initial steps array to avoid direct modification
  const updatedInitialSteps = JSON.parse(JSON.stringify(initialSteps));
  if (
    previousState &&
    previousState.context &&
    previousState.context.initialSteps
  ) {
    for (const step of previousState.context.initialSteps) {
      const stepIndex = updatedInitialSteps.findIndex(
        (s) => s.name === step.name
      );
      if (stepIndex < 0) continue;
      for (const field of step.fields) {
        const fieldIndex = updatedInitialSteps[stepIndex].fields.findIndex(
          (f) => f.name === field.name
        );
        if (fieldIndex < 0) continue;
        // Deep copy the field object before updating its value
        const updatedField = {
          ...updatedInitialSteps[stepIndex].fields[fieldIndex],
          value: field.value,
        };
        // Update the copied field object back into the copied updatedInitialSteps array
        updatedInitialSteps[stepIndex].fields[fieldIndex] = updatedField;
        /** I keep this as a reminder that errors should not be retrieved from the localStorage but 'obviously' re-computed :)
         * updatedInitialSteps[stepIndex].fields[fieldIndex].errors = field.errors;
         */
      }
    }
  }
  return {
    initialSteps: updatedInitialSteps,
    globalFields: getGlobalFieldsFromStorage({ globalFields, previousState }),
  };
};

/**
 * prefixes all step paths with the rootUrl and ensure the final path has a correct Url format
 */
const useUrlNavigation = (
  steps: ComparatorProps['steps'],
  { rootUrl }: IComparatorContext
): ComparatorProps['steps'] => {
  return useMemo(
    () =>
      steps.map((step) => {
        // remove the / at the beginning of step.path
        const cleanPath = step.path.startsWith('/')
          ? step.path.slice(1)
          : step.path;
        // add / at the end of the rootUrl
        // it ensures that the concatenated string has the correct format (as long as the rootUrl starts with /)
        const cleanRootUrl = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';
        const path = cleanRootUrl + cleanPath;
        return { ...step, path };
      }),
    [steps, rootUrl]
  );
};

const useNavigation = (
  steps: ComparatorProps['steps'],
  comparatorConfig: IComparatorContext
): ComparatorProps['steps'] => {
  return comparatorConfig.navigationMode === 'url'
    ? useUrlNavigation(steps, comparatorConfig)
    : steps;
};

const getIndexFromCurrentRoute = (
  steps: ComparatorProps['steps'],
  { navigationMode }: IComparatorContext,
  defaultStep: string
) => {
  if (navigationMode === 'url')
    return steps.findIndex(
      (step) =>
        step.path === location.pathname || step.path + '/' === location.pathname
    );
  else
    return steps.findIndex((step) => {
      const stepParam = new URLSearchParams(location.search).get('step');
      if (!stepParam) return step.path === defaultStep;
      return stepParam && step.path === stepParam;
    });
};

const useModules = (modules: Module[], contexts: ActionContexts) => {
  const [loadedModules, setLoadedModules] = useState<string[]>([]);
  const [allModulesLoaded, setAllModulesLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (contexts.comparator.navigate)
      for (const module of modules) {
        if (typeof module === 'function') module(contexts);
        if (typeof module === 'object' && module.on === undefined) {
          module.module(contexts);
        }
      }
  }, [contexts.comparator.navigate]);

  return {
    loadedModules,
    setLoadedModules,
    allModulesLoaded,
    setAllModulesLoaded,
  };
};

const setupApolloClient = (api: ComparatorProps['api']) => {
  // Initializing apollo client
  const cache = new InMemoryCache();
  const httpLink = createHttpLink({
    uri: api.baseUrl,
  });
  const authLink = setContext(async (_, { headers }) =>
    (api as GraphComparisonApiService<GraphLegacyApiService>).getHeaders(
      headers
    )
  );
  persistCacheSync({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
  });
  const client = new ApolloClient({
    link: authLink.concat(httpLink),
    cache,
  });

  (api as GraphComparisonApiService<GraphLegacyApiService>).setClient(client);
};

const ApolloProviderWrapper = ({
  api,
  children,
}: React.PropsWithChildren<{ api: ComparatorProps['api'] }>) => (
  <ApolloContextProvider
    client={
      (
        api as GraphComparisonApiService<GraphLegacyApiService>
      ).getClient() as ApolloClient<any>
    }
  >
    {children}
  </ApolloContextProvider>
);

const useApolloProvider = (api: ComparatorProps['api']) => {
  const isGraphApi = api instanceof GraphComparisonApiService;

  if (isGraphApi) {
    setupApolloClient(api);
  }

  return useCallback(
    ({ children }) =>
      isGraphApi ? (
        <ApolloProviderWrapper api={api}>{children}</ApolloProviderWrapper>
      ) : (
        children
      ),
    [api, isGraphApi]
  );
};

const loadLogger = (appName: string, api: ComparatorProps['api']) => {
  let url = process.env.KIZI_PUBLIC_LOGGER_BASE_URL || '';
  if (url !== '') {
    let jwt = `Bearer ${api.credentials.token}`;
    KiziLogger.initialize(url, appName);
    KiziLogger.setJwt(jwt);
    KiziLogger.setClientId(api.credentials.clientId || '');
  }
};

const useComparatorContext = ({
  name,
  api,
  modules = [],
  router = {},
  rootUrl = '/',
  navigationMode = 'slug',
}: ComparatorProps) =>
  useState<IBaseComparatorContext>({
    ...defaultComparatorContext,
    name,
    router,
    // TODO: deprecate those 2 functions since 'storage' can be used now
    getStateFromLocalStorage: getStateFromLocalStorage(name),
    persistStateInLocalStorage: persistStateInLocalStorage(name),
    navigate: undefined,
    rootUrl,
    navigationMode,
    api,
    storage: getStorage({
      getStateFromLocalStorage: getStateFromLocalStorage(name),
      persistStateInLocalStorage: persistStateInLocalStorage(name),
    }),
    modules: [...modules, ...defaultModules],
    loadedModules: [],
    allModulesLoaded: false,
  });

export const Comparator: React.FC<ComparatorProps> = ({
  api,
  steps: initialSteps,
  layout,
  loadFn,
  name,
  onDone,
  onTransition,
  wizardCallback,
  validators,
  transformers,
  formConfigs = {},
  execute = true,
  globalFields,
  modules = [],
  defaultStep = 'start',
  getPeregrineSetupEvent,
  isPartner = false,
  ...props
}) => {
  const previousState = useMemo(() => getStateFromLocalStorage(name)(), [name]);
  const [comparatorContext, setComparatorContext] = useComparatorContext({
    ...props,
    api,
    steps: initialSteps,
    layout,
    loadFn,
    name,
    onDone,
    onTransition,
    wizardCallback,
    validators,
    transformers,
    formConfigs,
    execute,
    globalFields,
    modules,
    defaultStep,
    getPeregrineSetupEvent,
    isPartner,
  });

  const steps = useNavigation(initialSteps, comparatorContext);

  const stepIndexFromCurrentRoute: number = useMemo(
    () => getIndexFromCurrentRoute(steps, comparatorContext, defaultStep),
    [steps, comparatorContext]
  );
  const {
    initialSteps: updatedInitialSteps,
    globalFields: updatedGlobalFields,
  } = useMemo(
    () =>
      getErrorsAndValuesFromLocalStorage(
        { steps, globalFields },
        previousState
      ),
    [steps, previousState]
  );

  const wizardService = useWizard(
    updatedInitialSteps,
    {
      startIndex:
        stepIndexFromCurrentRoute > -1 ? stepIndexFromCurrentRoute : 0,
      onDone,
      onTransition: (state, event) => {
        if (onTransition) onTransition(state, event);
        persistOnTransition(name)(state, event);
      },
      execute,
      validators,
      transformers,
      formConfigs,
      globalFields: updatedGlobalFields,
    },
    previousState
  );

  useEffect(() => {
    window.onpageshow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    localStorage.removeItem('apollo-cache-persist');

    try {
      if (getPeregrineSetupEvent) {
        (
          api as GraphComparisonApiService<GraphLegacyApiService>
        ).sendPeregrineEvent(getPeregrineSetupEvent());
      }
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    if (wizardCallback) wizardCallback(wizardService);
  }, [wizardService]);

  const pageBuilderContext: IPageBuilderContext = useMemo(
    () => ({ loadFn }),
    [loadFn]
  );

  const comparatorContextModuleAttributes = useModules(
    [...defaultModules, ...modules],
    {
      comparator: comparatorContext,
      wizard: wizardService,
      pageBuilder: pageBuilderContext,
    }
  );

  wizardService?.onTransition((_s, e) => {
    const { setLoadedModules, loadedModules } =
      comparatorContextModuleAttributes;
    if (e.type.includes('MODULE_LOADED') && !loadedModules.includes(e.type)) {
      setLoadedModules((prevState) => [...prevState, e.type]);
    }
  });

  const ApolloProvider = useApolloProvider(api);
  loadLogger(name, api);
  window.onerror = function (msg, url, line, col, error) {
    if (KiziLogger.logApiUrl) {
      KiziLogger.logError({
        message: JSON.stringify(msg),
        eventId: 0,
        eventName: 'Unhandled error',
        member: api.credentials.clientId as string,
        data: { url, line, col, error },
      });
    }
  };

  return (
    <>
      {/* ComparatorContext: global (top-level) context of the application, handles the Config (navigationMode...) * As it is the startup config it
      will most likely not change during the lifetime of the application, so it will most likely never trigger a re-render */}
      <ComparatorContext.Provider
        value={{
          ...comparatorContext,
          setComparatorContext,
          ...comparatorContextModuleAttributes,
          isPartner,
        }}
      >
        <ApolloProvider>
          {/* WizardContext: anything related to the steps config (forms, fields, wizard/form machine transitions like SUBMIT, GOTO etc)
           * That context is constantly changing, that's why XState selectors are used to only re-render components that were actually affected by a change
           */}
          <WizardContext.Provider value={{ wizardService }}>
            {/* PageBuilderContext: anything related to the layout and the PageBuilder/ComponentRenderer
             * same as the ComparatorContext, the values in there will most likely not change, so no big deal about re-rendering
             */}
            <PageBuilderContext.Provider value={pageBuilderContext}>
              <Router>
                <Routing layout={layout} steps={steps} />
              </Router>
            </PageBuilderContext.Provider>
          </WizardContext.Provider>
        </ApolloProvider>
      </ComparatorContext.Provider>
    </>
  );
};
